<template>
    <div v-if="visible" class="admin-floating-bar">
        <el-popover
            placement="top-end"
            :width="200"
            trigger="click"
            :show-arrow="false"
            popper-class="admin-floating-popover"
        >
            <template #reference>
                <el-button
                    class="admin-fab"
                    circle
                    type="primary"
                    :icon="Setting"
                    size="large"
                />
            </template>
            <div class="admin-menu">
                <div class="admin-menu-title">{{ $t('usageManagement.page.title') }}</div>
                <div
                    v-for="item in menuItems"
                    :key="item.tab"
                    class="admin-menu-item"
                    @click="navigateTo(item.tab)"
                >
                    <el-icon :size="16"><component :is="item.icon" /></el-icon>
                    <span>{{ item.label }}</span>
                </div>
            </div>
        </el-popover>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Setting, User, Tools, Document, DataAnalysis, TrendCharts, Coin, ChatDotRound } from '@element-plus/icons-vue';
import { useCurrentUser } from '@/composables/useCurrentUser';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { isSuperAdminUser } = useCurrentUser();

const isAdminPage = computed(() => route.path.startsWith('/admin/'));

const visible = computed(() => isSuperAdminUser.value && !isAdminPage.value);

const menuItems = computed(() => [
    { tab: 'overview', label: t('usageManagement.tabs.overview'), icon: DataAnalysis },
    { tab: 'token-usage', label: t('usageManagement.tabs.tokenUsage'), icon: Coin },
    { tab: 'usage', label: t('usageManagement.tabs.usage'), icon: User },
    { tab: 'report', label: t('usageManagement.tabs.report'), icon: TrendCharts },
    { tab: 'model', label: t('usageManagement.tabs.model'), icon: Setting },
    { tab: 'tool', label: t('usageManagement.tabs.tool'), icon: Tools },
    { tab: 'prompt', label: t('usageManagement.tabs.prompt'), icon: Document },
    { tab: 'feedback', label: t('usageManagement.tabs.feedback'), icon: ChatDotRound },
]);

function navigateTo(tab: string): void {
    router.push({ path: '/admin/usage-management', query: { tab } });
}
</script>

<style scoped lang="scss">
@import '@/styles/variables.scss';

.admin-floating-bar {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 9998;

    .admin-fab {
        width: 48px;
        height: 48px;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
        background: linear-gradient(135deg, $brand-primary 0%, darken($brand-primary, 10%) 100%);
        border: none;
        transition: all 0.3s ease;

        &:hover {
            transform: scale(1.1);
            box-shadow: 0 6px 20px rgba($brand-primary, 0.4);
        }
    }
}

.admin-menu {
    .admin-menu-title {
        font-size: 13px;
        font-weight: 600;
        color: $text-secondary;
        padding: 4px 8px 8px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.06);
        margin-bottom: 4px;
    }

    .admin-menu-item {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 8px;
        border-radius: 6px;
        cursor: pointer;
        font-size: 14px;
        color: $text-primary;
        transition: all 0.2s ease;

        &:hover {
            background: rgba($brand-primary, 0.08);
            color: $brand-primary;
        }
    }
}
</style>
